import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getDashboardStats, getTodayAbsensi, syncPendingAttendance, getTodayFotoCount } from '../../lib/api';
import { getPendingCount } from '../../lib/db';
import { getGreeting, formatTime, getStatusLabel, getStatusColor } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import ProtectedImage from '../ui/ProtectedImage';
import { 
  Camera, Clock, MapPin, CheckCircle, XCircle, AlertCircle,
  RefreshCw, Wifi, WifiOff, Calendar, TrendingUp, Image, User
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../ui/sonner';

const GuruDashboard = () => {
  const { user, isOnline } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [todayAbsensi, setTodayAbsensi] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [fotoCount, setFotoCount] = useState({ count: 0, max: 3 });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    loadData();
    loadPendingCount();
    
    // Try auto-sync on page load if online
    if (isOnline) {
      handleSync();
    }
  }, []);

  useEffect(() => {
    // Auto-sync when coming online (only if there are pending items)
    if (isOnline && pendingCount > 0 && !syncing) {
      handleSync();
    }
  }, [isOnline]);
  
  // Refresh pending count periodically
  useEffect(() => {
    const interval = setInterval(() => {
      loadPendingCount();
    }, 5000); // Check every 5 seconds
    
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsData, absensiData, fotoData] = await Promise.all([
        getDashboardStats().catch(() => null),
        getTodayAbsensi().catch(() => []),
        getTodayFotoCount().catch(() => ({ count: 0, max: 3 }))
      ]);
      if (statsData) setStats(statsData);
      setTodayAbsensi(absensiData || []);
      setFotoCount(fotoData || { count: 0, max: 3 });
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Gagal memuat data dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadPendingCount = async () => {
    const count = await getPendingCount();
    setPendingCount(count);
  };

  const handleSync = async () => {
    if (!isOnline) {
      toast.error('Tidak dapat sinkronisasi saat offline');
      return;
    }
    
    if (syncing) return; // Prevent double sync

    setSyncing(true);
    toast.info('Memulai sinkronisasi data...', { duration: 2000 });
    
    try {
      const result = await syncPendingAttendance();
      
      if (result.synced > 0) {
        toast.success(`✅ ${result.synced} absensi berhasil disinkronkan!`, {
          description: 'Data sudah tersimpan di server'
        });
      } else if (result.synced === 0 && result.failed === 0) {
        toast.info('Tidak ada data yang perlu disinkronkan');
      }
      
      if (result.failed > 0) {
        toast.warning(`⚠️ ${result.failed} absensi gagal disinkronkan`, {
          description: 'Akan dicoba lagi nanti'
        });
      }
      
      // Refresh data after sync
      await Promise.all([loadData(), loadPendingCount()]);
      
    } catch (error) {
      console.error('Sync error:', error);
      toast.error('Gagal melakukan sinkronisasi', {
        description: error.message || 'Silakan coba lagi'
      });
    } finally {
      setSyncing(false);
    }
  };

  const sudahAbsenMasuk = stats?.sudah_absen_masuk || todayAbsensi.some(a => a.tipe === 'masuk');
  const sudahAbsenPulang = stats?.sudah_absen_pulang || todayAbsensi.some(a => a.tipe === 'pulang');

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto" data-testid="guru-dashboard">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-gray-500">{getGreeting()},</p>
            <h1 className="text-2xl md:text-3xl font-bold text-indigo-950">{user?.nama}</h1>
          </div>
          <div className="flex items-center gap-2">
            {isOnline ? (
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                <Wifi className="w-3 h-3 mr-1" /> Online
              </Badge>
            ) : (
              <Badge className="bg-red-100 text-red-800 border-red-200">
                <WifiOff className="w-3 h-3 mr-1" /> Offline
              </Badge>
            )}
          </div>
        </div>
        <p className="text-gray-600">{stats?.sekolah?.nama || 'Memuat...'}</p>
      </div>

      {/* Pending Sync Alert */}
      {pendingCount > 0 && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <AlertCircle className="w-6 h-6 text-amber-600" />
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {pendingCount}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-amber-800">{pendingCount} absensi menunggu sinkronisasi</p>
                  <p className="text-sm text-amber-600">
                    {isOnline 
                      ? 'Klik tombol untuk sinkronkan sekarang' 
                      : 'Hubungkan ke internet untuk sinkronisasi'}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                onClick={handleSync}
                disabled={!isOnline || syncing}
                className={`${isOnline ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-gray-300 text-gray-500'}`}
                data-testid="sync-btn"
              >
                {syncing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Sinkronisasi...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-1" /> Sinkronkan Sekarang
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Manual Refresh Button when no pending but want to refresh data */}
      {pendingCount === 0 && (
        <div className="mb-4 flex justify-end">
          <Button
            size="sm"
            variant="outline"
            onClick={loadData}
            disabled={loading}
            className="border-indigo-200 text-indigo-700"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh Data
          </Button>
        </div>
      )}

      {/* Main Attendance Action */}
      <Card className="mb-6 border-indigo-200 overflow-hidden">
        <div className="hero-gradient p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-indigo-200 text-sm">Hari ini</p>
              <p className="text-xl font-bold">{new Date().toLocaleDateString('id-ID', { 
                weekday: 'long', 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric' 
              })}</p>
            </div>
            <Clock className="w-10 h-10 text-amber-400" />
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-white/10 rounded-lg p-3">
              <p className="text-indigo-200 text-xs">Absen Masuk</p>
              <div className="flex items-center gap-2 mt-1">
                {sudahAbsenMasuk ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span className="font-semibold">Sudah</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5 text-red-400" />
                    <span className="font-semibold">Belum</span>
                  </>
                )}
              </div>
            </div>
            <div className="bg-white/10 rounded-lg p-3">
              <p className="text-indigo-200 text-xs">Absen Pulang</p>
              <div className="flex items-center gap-2 mt-1">
                {sudahAbsenPulang ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span className="font-semibold">Sudah</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-5 h-5 text-gray-400" />
                    <span className="font-semibold">Belum</span>
                  </>
                )}
              </div>
            </div>
          </div>

          {(!sudahAbsenMasuk || !sudahAbsenPulang) && (
            <Button
              className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-indigo-950 font-bold text-lg pulse-attention"
              onClick={() => navigate('/absensi')}
              data-testid="absen-btn"
            >
              <Camera className="w-6 h-6 mr-2" />
              {!sudahAbsenMasuk ? 'Absen Masuk Sekarang' : 'Absen Pulang Sekarang'}
            </Button>
          )}

          {sudahAbsenMasuk && sudahAbsenPulang && (
            <div className="bg-emerald-500/20 rounded-lg p-4 text-center">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
              <p className="font-semibold">Absensi Hari Ini Lengkap</p>
            </div>
          )}
        </div>
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card className="border-indigo-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-950">{stats?.total_hadir_bulan_ini || 0}</p>
                <p className="text-xs text-gray-500">Hadir Bulan Ini</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-indigo-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                fotoCount.count >= 3 ? 'bg-emerald-100' : 'bg-amber-100'
              }`}>
                <Image className={`w-5 h-5 ${
                  fotoCount.count >= 3 ? 'text-emerald-600' : 'text-amber-600'
                }`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-950">{fotoCount.count}/{fotoCount.max}</p>
                <p className="text-xs text-gray-500">Foto Kegiatan</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-indigo-100/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-indigo-950">-</p>
                <p className="text-xs text-gray-500">Skor Kinerja</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Foto Kegiatan Reminder */}
      {sudahAbsenMasuk && fotoCount.count < 3 && (
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Image className="w-6 h-6 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">Upload Foto Kegiatan</p>
                  <p className="text-sm text-amber-600">Anda belum upload {3 - fotoCount.count} foto lagi</p>
                </div>
              </div>
              <Button
                size="sm"
                className="bg-amber-500 hover:bg-amber-600 text-white"
                onClick={() => navigate('/foto-kegiatan')}
                data-testid="upload-foto-btn"
              >
                <Camera className="w-4 h-4 mr-1" />
                Upload
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Today's Attendance History */}
      <Card className="border-indigo-100/50">
        <CardHeader>
          <CardTitle className="text-lg">Absensi Hari Ini</CardTitle>
        </CardHeader>
        <CardContent>
          {todayAbsensi.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Camera className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Belum ada absensi hari ini</p>
            </div>
          ) : (
            <div className="space-y-3">
              {todayAbsensi.map((absen) => (
                <div
                  key={absen.id}
                  className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg"
                >
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                    {absen.foto_url && (
                      <ProtectedImage 
                        src={absen.foto_url} 
                        alt="Foto absensi" 
                        className="w-full h-full"
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium capitalize">{absen.tipe}</span>
                      <Badge className={`text-xs ${getStatusColor(absen.status)}`}>
                        {getStatusLabel(absen.status)}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-500">{formatTime(absen.waktu_absen)}</p>
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                      <MapPin className="w-3 h-3" />
                      {absen.dalam_radius ? 'Dalam radius sekolah' : 'Di luar radius sekolah'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-4 mt-6">
        <Button
          variant="outline"
          className="h-12 border-indigo-200"
          onClick={() => navigate('/foto-kegiatan')}
          data-testid="foto-kegiatan-btn"
        >
          <Image className="w-4 h-4 mr-2" />
          Foto Kegiatan
        </Button>
        <Button
          variant="outline"
          className="h-12 border-indigo-200"
          onClick={() => navigate('/riwayat')}
          data-testid="riwayat-btn"
        >
          <Calendar className="w-4 h-4 mr-2" />
          Riwayat Absensi
        </Button>
        <Button
          variant="outline"
          className="h-12 border-indigo-200"
          onClick={() => navigate('/izin')}
          data-testid="izin-btn"
        >
          <AlertCircle className="w-4 h-4 mr-2" />
          Ajukan Izin
        </Button>
        <Button
          variant="outline"
          className="h-12 border-indigo-200"
          onClick={() => navigate('/profil')}
          data-testid="profil-btn"
        >
          <User className="w-4 h-4 mr-2" />
          Profil & Notifikasi
        </Button>
      </div>
    </div>
  );
};

export default GuruDashboard;
